import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import './index.css';
import { widget } from '../../charting_library/charting_library.esm';
import { AnalystBroDatafeed } from '../../datafeeds/udf/lib/analystbro-datafeed';
import { API_BASE_URL } from '../../config';

function getLanguageFromURL() {
    const regex = new RegExp('[\\?&]lang=([^&#]*)');
    const results = regex.exec(window.location.search);
    return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

const EVENT_CATEGORY_STYLE = {
    candlestick:      { color: '#26a69a', shape: 'arrow_up', bearColor: '#ef5350', bearShape: 'arrow_down' },
    chart_pattern:    { color: '#26a69a', shape: 'arrow_up', bearColor: '#ef5350', bearShape: 'arrow_down' },
    trend_ma:         { color: '#2196f3', shape: 'arrow_up', bearColor: '#ff9800', bearShape: 'arrow_down' },
    oscillator:       { color: '#ab47bc', shape: 'arrow_up', bearColor: '#ab47bc', bearShape: 'arrow_down' },
    volume:           { color: '#78909c', shape: 'arrow_up', bearColor: '#78909c', bearShape: 'arrow_down' },
    market_structure: { color: '#26a69a', shape: 'arrow_up', bearColor: '#ef5350', bearShape: 'arrow_down' },
    boundary:         { color: '#26a69a', shape: 'arrow_up', bearColor: '#ef5350', bearShape: 'arrow_down' },
    statistical:      { color: '#ffeb3b', shape: 'arrow_up', bearColor: '#ffeb3b', bearShape: 'arrow_down' },
    levels:           { color: '#00bcd4', shape: 'arrow_up', bearColor: '#00bcd4', bearShape: 'arrow_down' },
};

export const TVChartContainer = forwardRef((props, ref) => {
    const chartContainerRef = useRef();
    const tvWidgetRef = useRef(null);
    const eventShapeIdsRef = useRef([]);   // [{id, shapeId}] for cleanup

    useImperativeHandle(ref, () => ({
        captureScreenshot: async () => {
            if (!tvWidgetRef.current) return null;
            try {
                const canvas = await tvWidgetRef.current.takeClientScreenshot({
                    backgroundColor: props.theme === 'Dark' ? '#131722' : '#ffffff',
                    borderColor: '#cccccc',
                    fontSize: 12,
                    legendMode: 'complete',
                    hideResolution: false,
                });
                return canvas.toDataURL('image/png');
            } catch {
                return null;
            }
        },
        clearMarks: () => {
            tvWidgetRef.current?.activeChart()?.clearMarks();
        },
        refreshMarks: () => {
            tvWidgetRef.current?.activeChart()?.refreshMarks();
        },
        clearAndRefreshMarks: () => {
            const chart = tvWidgetRef.current?.activeChart();
            if (!chart) return;
            chart.clearMarks();
            // Bypass TV's 300ms debounce by calling _getData directly on the internal
            // BarsMarksContainer. After clearMarks(), _lastRange is preserved but
            // _loadedRange is null, so _rangeDifference returns the full range.
            //
            // IMPORTANT: TV's getData() normally extends `to` to _maxDate (year 2037)
            // before calling _getData. We must do the same here so the range matches
            // what the datafeed cached from the initial server fetch. Without this,
            // the cache key mismatches and every removal triggers a server round-trip.
            const MAX_TS = 2114380800; // Jan 1 2037 — TV's internal _maxDate
            try {
                const sources = chart._chartWidget?.model()?.barsMarksSources?.();
                sources?.forEach(s => {
                    if (s._lastRange) {
                        s._getData({ start: s._lastRange.start, end: s._maxDate || MAX_TS });
                    }
                });
            } catch {
                chart.refreshMarks();
            }
        },
        setSymbol: (ticker, interval) => {
            if (!tvWidgetRef.current) return;
            tvWidgetRef.current.activeChart().setSymbol(ticker, interval ?? undefined);
        },
        createEventMarkers: async (events) => {
            const chart = tvWidgetRef.current?.activeChart();
            if (!chart) return;
            // Remove any previously created event markers first
            eventShapeIdsRef.current.forEach(({ shapeId }) => {
                try { chart.removeEntity(shapeId); } catch {}
            });
            eventShapeIdsRef.current = [];

            for (const evt of events) {
                try {
                    const ts = Math.floor(new Date(evt.occurred_at).getTime() / 1000);
                    const style = EVENT_CATEGORY_STYLE[evt.event_category] || EVENT_CATEGORY_STYLE.boundary;
                    const isBear = evt.direction === 'bearish';
                    const color = isBear ? style.bearColor : style.color;
                    const shape = isBear ? style.bearShape : style.shape;
                    const shapeId = await chart.createShape(
                        { time: ts },
                        {
                            shape,
                            overrides: { color, size: 1 },
                            text: evt.event_type,
                        }
                    );
                    if (shapeId) {
                        eventShapeIdsRef.current.push({ shapeId });
                    }
                } catch {}
            }
        },
        clearEventMarkers: () => {
            const chart = tvWidgetRef.current?.activeChart();
            if (!chart) return;
            eventShapeIdsRef.current.forEach(({ shapeId }) => {
                try { chart.removeEntity(shapeId); } catch {}
            });
            eventShapeIdsRef.current = [];
        },
    }));

    useEffect(() => {
        const token = localStorage.getItem('token');
        const storageBase = `${API_BASE_URL}/tv/1.1`;

        const authFetch = (url, options = {}) => fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                ...(options.headers || {}),
            },
        }).then(r => r.json());

        const save_load_adapter = {
            getAllCharts: () =>
                authFetch(`${storageBase}/charts`).then(r => r.data || []),
            getChartContent: (chartId) =>
                authFetch(`${storageBase}/charts?chart=${chartId}`).then(r => r.data.content),
            saveChart: (chartData) =>
                authFetch(`${storageBase}/charts${chartData.id ? `?chart=${chartData.id}` : ''}`, {
                    method: 'POST', body: JSON.stringify(chartData),
                }).then(r => r.id),
            removeChart: (chartId) =>
                authFetch(`${storageBase}/charts?chart=${chartId}`, { method: 'DELETE' }),
            getAllStudyTemplates: () =>
                authFetch(`${storageBase}/study_templates`).then(r => r.data || []),
            getStudyTemplateContent: (template) =>
                authFetch(`${storageBase}/study_templates?template=${encodeURIComponent(template.name)}`).then(r => r.data.content),
            saveStudyTemplate: (template) =>
                authFetch(`${storageBase}/study_templates`, { method: 'POST', body: JSON.stringify(template) }),
            removeStudyTemplate: (template) =>
                authFetch(`${storageBase}/study_templates?template=${encodeURIComponent(template.name)}`, { method: 'DELETE' }),
            getAllDrawingTemplates: (toolName) =>
                authFetch(`${storageBase}/drawing_templates?tool=${encodeURIComponent(toolName)}`).then(r => r.data || []),
            getDrawingTemplateContent: (toolName, templateName) =>
                authFetch(`${storageBase}/drawing_templates?tool=${encodeURIComponent(toolName)}&name=${encodeURIComponent(templateName)}`).then(r => r.data.content),
            saveDrawingTemplate: (toolName, template) =>
                authFetch(`${storageBase}/drawing_templates`, { method: 'POST', body: JSON.stringify({ tool: toolName, ...template }) }),
            removeDrawingTemplate: (toolName, templateName) =>
                authFetch(`${storageBase}/drawing_templates?tool=${encodeURIComponent(toolName)}&name=${encodeURIComponent(templateName)}`, { method: 'DELETE' }),
        };

        const datafeed = new AnalystBroDatafeed(
            `${API_BASE_URL}/api/v1/charts`,
            { access: token, refresh: null },
            props.overlayStateRef,
        );

        const tvWidget = new widget({
            symbol: props.symbol || 'AAPL',
            datafeed,
            interval: props.interval || 'D',
            container: chartContainerRef.current,
            library_path: '/charting_library/',
            locale: getLanguageFromURL() || 'en',
            disabled_features: ['use_localstorage_for_settings'],
            enabled_features: ['study_templates', 'drawing_templates', 'side_toolbar_in_fullscreen_mode'],
            save_load_adapter,
            fullscreen: false,
            autosize: true,
            studies_overrides: {},
            theme: props.theme || 'Light',
        });

        tvWidgetRef.current = tvWidget;

        tvWidget.onChartReady(() => {
            if (props.onSymbolChange) {
                tvWidget.activeChart().onSymbolChanged().subscribe(null, () => {
                    const si = tvWidget.activeChart().symbolExt();
                    if (si) props.onSymbolChange(si.ticker || si.name);
                });
                // emit initial symbol
                const si = tvWidget.activeChart().symbolExt();
                if (si) props.onSymbolChange(si.ticker || si.name);
            }
            tvWidget.subscribe('onMarkClick', (markId) => {
                if (props.onMarkClick) props.onMarkClick(markId);
            });

            // plan 181 §B3: register the command registry UNCONDITIONALLY so the
            // headless CDP launcher can drive this chart with no WS bridge running.
            // `window._tvApiBase` is also needed by getBars; set it here (the WS
            // bridge sets it too, but it must exist even when the bridge is off).
            Promise.all([
                import('./brochartRegistry.js'),
                import('./brochartSidebar.js'),
            ]).then(([{ createBrochartRegistry }, { registerBrochartSidebarHandlers }]) => {
                window._tvApiBase = import.meta.env.VITE_API_BASE_URL || window.location.origin;
                window.__brochart = createBrochartRegistry(tvWidget);
                registerBrochartSidebarHandlers(window.__brochart);  // sidebar cmds (plan 181)
                window.dispatchEvent(new CustomEvent('brochart:ready'));
            });

            // The interactive WS bridge stays an opt-in transport (dev / hosted
            // human tab); it now routes incoming cmd frames into window.__brochart.
            if (import.meta.env.VITE_TV_BRIDGE === 'true') {
                import('./chart-bridge.js').then(({ ChartBridge }) => {
                    window._tvBridge = new ChartBridge(tvWidget);
                });
            }
        });

        return () => {
            tvWidgetRef.current?.remove();
            tvWidgetRef.current = null;
        };
    }, [props.symbol, props.interval, props.theme]);

    return (
        <div
            ref={chartContainerRef}
            className={'TVChartContainer'}
            style={{ height: '100%', width: '100%' }}
        />
    );
});

export default TVChartContainer;
